import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { SwiperSlide } from "swiper/react";
import { useTranslation } from "react-i18next";

import { messages, EMPTY_STRING } from "../../../constants";
import { ReactComponent as PlayIcon } from "../../../assets/icons/play.svg";
import { ReactComponent as StarIcon } from "../../../assets/icons/star-fill.svg";
import { ReactComponent as PhoneIcon } from "../../../assets/icons/capture-event.svg";
import { ReactComponent as ChevronLeftIcon } from "../../../assets/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "../../../assets/icons/chevron-right.svg";
import { isVideoUrl, truncateString } from "../../utility";
import { eventBookingActions } from "../../../redux/actions";

import IconButton from "../IconButton/IconButton";
import SliderWrapper from "../Slider/SliderWrapper";
import ProfileImage from "../ProfileImage/ProfileImage";
import MediaPreviewModal from "../Modal/MediaPreviewModal";

const ShutterBSelectionCard = ({
  shutterB,
  onSelect,
  isSelected,
  onViewProfileClick,
}) => {
  const { t } = useTranslation("common");

  const textRef = useRef(null);
  const [isClamped, setIsClamped] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCardClick = () => {
    onSelect();
  };

  const handleViewProfileClick = (e) => {
    e.stopPropagation();

    if (onViewProfileClick) {
      onViewProfileClick(shutterB.sbUserId, !!isSelected);
    }
  };

  const getGalleryItems = () => {
    const baseUrl = process.env.REACT_APP_S3_BASE_URL_PORTFOLIO;
    const galleries = shutterB?.sbportfolioGalleries[0]?.gallery ?? [];

    return galleries.map((gallery) => ({
      ...gallery,
      url: gallery.imageUrl
        ? `${baseUrl}/${gallery.imageUrl}`
        : gallery.imageUrl,
      thumbnailUrl: gallery.imageThumbnailUrl
        ? `${baseUrl}/${gallery.imageThumbnailUrl}`
        : gallery.imageThumbnailUrl,
    }));
  };

  useEffect(() => {
    if (textRef.current) {
      const el = textRef.current;
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const totalLines = Math.round(el.scrollHeight / lineHeight);

      setIsClamped(totalLines > 4);
    }
    // eslint-disable-next-line
  }, [shutterB?.sbportfolioGalleries]);

  return (
    <div
      className={`shutterb-selection-card ${
        isSelected ? "selected-card" : EMPTY_STRING
      }`}
    >
      <div className="row">
        <div className="col-12 col-lg custom-order-profile order-2 position-relative shutterb-info">
          <div className="d-flex align-items-start">
            <div className="flex-shrink-0 profile-image">
              <ProfileImage
                imageUrl={
                  shutterB?.sbportfolioGalleries?.[0]?.profileImageUrl ||
                  EMPTY_STRING
                }
              />
            </div>
            <div>
              <p className="shutterb-name text-break mt-2 mb-0">
                {truncateString(`${shutterB?.firstName} ${shutterB?.lastName}`, 25)}
              </p>

              <div>
                <StarIcon className="star-icon" />
                <span className="shuttberb-info ml-2">
                  {shutterB.averageReview
                    ? Number(shutterB.averageReview)?.toFixed(1)
                    : "0.0"}
                  /5
                </span>
              </div>

              <div className="line-height-normal">
                <PhoneIcon className="star-icon" />
                <span className="shuttberb-info text-break ml-2">
                  {shutterB.device?.isActive
                    ? shutterB.device?.deviceName
                    : EMPTY_STRING}
                </span>
              </div>
            </div>
          </div>

          <p className="shutterb-description my-2">
            {shutterB?.sbportfolioGalleries[0]?.bio && (
              <>
                <span ref={textRef} className="clamped-text">
                  {shutterB?.sbportfolioGalleries[0].bio}
                </span>
                {isClamped && (
                  <button
                    className="button-with-only-text"
                    onClick={handleViewProfileClick}
                  >
                    Read More
                  </button>
                )}
              </>
            )}
          </p>

          <div className="profile-btn d-flex justify-content-center justify-content-sm-start align-items-center mt-4 w-100">
            <button
              className="button-common button-outline"
              onClick={handleViewProfileClick}
            >
              {t("view_profile")}
            </button>
            <button
              className="button-common button-fill ml-2"
              onClick={handleCardClick}
            >
              {isSelected ? messages.UNRESERVE : messages.RESERVE}
            </button>
          </div>
        </div>
        <div className="col-12 col-lg-auto custom-order-slider order-1 mb-2 mb-lg-0 p-0 fixed-slider-col p-1">
          <div
            className="slider-container"
            onClick={(e) => e.stopPropagation()}
          >
            {getGalleryItems()?.length > 0 && (
              <SliderWrapper
                spaceBetween={2}
                sliderHeight={"360px"}
                PrevIcon={ChevronLeftIcon}
                NextIcon={ChevronRightIcon}
                prevButtonClass="custom-swiper-button custom-swiper-button-prev"
                nextButtonClass="custom-swiper-button custom-swiper-button-next"
              >
                {getGalleryItems().map((gallery, index) => {
                  const imagePath =
                    gallery.thumbnailUrl || gallery.url || EMPTY_STRING;

                  return (
                    <SwiperSlide key={index}>
                      <div className="video-thumbnail-wrapper h-100 w-100">
                        <img
                          className="h-100"
                          src={imagePath}
                          alt={`p-${index}`}
                          onClick={() => {
                            setCurrentIndex(index);
                            setIsModalOpen(true);
                          }}
                        />
                        {isVideoUrl(gallery.imageUrl) && (
                          <div className="play-icon-overlay">
                            <IconButton
                              icon={<PlayIcon />}
                              width="35px"
                              height="35px"
                              className="shutterb-profile-play-icon"
                              handleClick={() => {
                                setCurrentIndex(index);
                                setIsModalOpen(true);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </SliderWrapper>
            )}
          </div>
        </div>
      </div>

      <MediaPreviewModal
        isOpen={isModalOpen}
        currentIndex={currentIndex}
        mediaFiles={getGalleryItems()}
        setCurrentIndex={setCurrentIndex}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  isProfileViewShow: state.eventBooking.isProfileViewShow,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setIsProfileViewShow: (data) =>
    dispatch(eventBookingActions.setIsProfileViewShow(data)),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ShutterBSelectionCard);
